import { fetchAndUpdateSessionExpiration as fetch } from 'platform/utilities/api';
import localStorage from 'platform/utilities/storage/localStorage';
import { getAPI, resolveParamsWithUrl } from '../config';

// vets-api returns a fresh CSRF token on every response
// (ApplicationController#set_csrf_header). LocatorApi uses the low-level fetch
// rather than apiRequest, so — unlike the rest of the app — it would otherwise
// never store that token. Persist it here so a later /va POST echoes a current
// token (and picks up a rotated one) instead of depending solely on other
// apiRequest traffic. Read before the ok-check so an error response's fresh
// token is still captured. Guarded so response stubs without headers are safe.
const persistCsrfToken = response => {
  const token = response?.headers?.get?.('X-CSRF-Token');
  if (token && token !== localStorage.getItem('csrfToken')) {
    localStorage.setItem('csrfToken', token);
  }
};

class LocatorApi {
  /**
   * Sends the request to vets-api to query which locations exist within the
   * given bounding box's area and optionally cenetered on the given address.
   *
   * Allows for filtering on location types and services provided.
   *
   * @param {string=} address The address associated with the bounding box's center
   * @param {number[]} bounds Array defining the bounding box of the search area
   * @param {string} locationType What kind of location? (i.e. facilityType or Provider)
   * @param {string} serviceType What services should the location provide?
   * @param {number} page Which page of results to start with?
   * @returns {Promise} Promise object
   */
  static searchWithBounds(
    address = null,
    bounds,
    locationType,
    serviceType,
    page,
    center,
    radius,
  ) {
    const { params, url, postParams } = resolveParamsWithUrl({
      address,
      locationType,
      serviceType,
      page,
      bounds,
      center,
      radius,
    });

    const api = getAPI();
    const startTime = new Date().getTime();
    return new Promise((resolve, reject) => {
      if (url.slice(-3) === '/va') {
        fetch(`${url}`, {
          // Reuse the shared request defaults (credentials, X-Key-Inflection,
          // Source-App-Name) from config so this POST can't drift from them;
          // add only the POST-specific Content-Type and the CSRF token.
          ...api.settings,
          headers: {
            ...api.settings.headers,
            'Content-Type': 'application/json',
            'X-CSRF-Token': localStorage.getItem('csrfToken'),
          },
          method: 'POST',
          body: JSON.stringify(postParams),
        })
          .then(response => {
            persistCsrfToken(response);
            if (!response.ok) {
              throw Error(response.statusText);
            }
            return response.json();
          })
          .then(res => {
            const endTime = new Date().getTime();
            const resultTime = endTime - startTime;
            res.meta = {
              ...res.meta,
              resultTime,
            };
            return res;
          })
          .then(
            data => resolve(data),
            error => reject(error),
          );
      } else {
        fetch(`${url}?${params}`, api.settings)
          .then(response => {
            persistCsrfToken(response);
            if (!response.ok) {
              throw Error(response.statusText);
            }
            return response.json();
          })
          .then(res => {
            const endTime = new Date().getTime();
            const resultTime = endTime - startTime;
            res.meta = {
              ...res.meta,
              resultTime,
            };
            return res;
          })
          .then(
            data => resolve(data),
            error => reject(error),
          );
      }
    });
  }

  /**
   * Get one VA Facililty's details.
   *
   * @param {string} id The ID of the Facility
   */
  static fetchVAFacility(id) {
    const api = getAPI();
    const url = `${api.url}/${id}`;

    return new Promise((resolve, reject) => {
      fetch(url, api.settings)
        .then(response => {
          persistCsrfToken(response);
          if (!response.ok) {
            throw Error(response.statusText);
          }
          return response.json();
        })
        .then(
          data => resolve(data),
          error => reject(error),
        );
    });
  }

  /**
   * Get all known specialties available from all CC Providers.
   */
  static getProviderSpecialties() {
    const api = getAPI();
    const url = `${api.baseUrl}/ccp/specialties`;
    return new Promise((resolve, reject) => {
      fetch(url, api.settings)
        .then(response => {
          persistCsrfToken(response);
          if (!response.ok) {
            throw Error(response.statusText);
          }
          return response.json();
        })
        .then(
          data => {
            // Optional-chain `data` so a null/undefined body rejects cleanly
            // below instead of throwing here (which, inside this success
            // callback, would leave the outer Promise unsettled — a hang).
            if (data?.errors?.length) {
              return reject(data.errors[0]);
            }
            if (!data?.data) {
              return reject(Error('Invalid response: missing data'));
            }
            return resolve(data.data.map(specialty => specialty.attributes));
          },
          error => reject(error),
        );
    });
  }
}

export default LocatorApi;
